import {
  AllFeatures,
  AllPrograms,
  SubscriptionBundlePrice,
  SubscriptionBundle,
  AllContent,
} from '../../common/bundle.types';
import { BundleItemType, BundlePricing } from 'generated/graphql';
import {
  BundlesVMDispatcher,
  BundleVMState,
} from './subscriptionBundles.viewModel';

export type BundleCardFreeTrial = {
  monthly: {
    checked: boolean;
    onChange: () => void;
  };
  annual: {
    checked: boolean;
    onChange: () => void;
  };
};

export type BundleCard = {
  name: string;
  bundleId: number;
  price: string;
  freeTrial?: BundleCardFreeTrial;
  showFreeTrial: boolean;
  tooltipText?: Array<string>;
};

export type BundlesCards = Array<BundleCard>;

export type BundleContent = {
  bundleId: number;
  checked: boolean;
  onChange: () => void;
  disabled?: boolean;
  tooltip?: string;
};

export type BundleSectionList = Array<{
  name: string;
  bundleContent: Array<BundleContent>;
}>;

export type BundleTableSection = {
  name: string;
  open: boolean;
  list: BundleSectionList;
};

export type BundleTableHeader = {
  bundleName: Array<string>;
  section: {
    open: boolean;
    toggle: () => void;
  };
};

export type BundlesTable = {
  header: BundleTableHeader;
  programs: BundleTableSection;
  features: BundleTableSection;
  content: BundleTableSection;
};

export const toTableSections = (
  dispatch: BundlesVMDispatcher,
  state: BundleVMState,
  subscriptionPrograms: AllPrograms,
  allFeatures: AllFeatures,
  allContent: AllContent,
  bundles: SubscriptionBundle[],
): BundlesTable => ({
  header: {
    bundleName: bundles.map((b) => b.title),
    section: {
      open: state.listSectionOpen,
      toggle: () => dispatch('toggleListSection'),
    },
  },
  programs: {
    open: state.programsSectionOpen,
    name: 'program',
    list: subscriptionPrograms.map((p) => ({
      name: p.name,
      bundleContent: bundles.map((b) => ({
        bundleId: b.id,
        checked: b.items.some((i) => i.itemId === p.id),
        onChange: () =>
          dispatch('toggleBundleContent', {
            bundleId: b.id,
            itemId: p.id,
            type: BundleItemType.Program,
          }),
        disabled:
          !b.isPlatformBundle && b.pricing === BundlePricing.Subscription,
        tooltip:
          !b.isPlatformBundle && b.pricing === BundlePricing.Subscription
            ? 'To remove program from your Exclusive Access you need to change its state to “Archived” in your “Subscription” tab'
            : undefined,
      })),
    })),
  },
  features: {
    open: state.featuresSectionsOpen,
    name: 'Other features',
    list: allFeatures.map((f) => ({
      name: f.name,
      bundleContent: bundles.map((b) => ({
        bundleId: b.id,
        disabled: PremiumBundleFeatureState(b, f.id, bundles),
        checked: b.items.some((i) => i.itemId === f.id),
        onChange: () =>
          dispatch('toggleBundleContent', {
            bundleId: b.id,
            itemId: f.id,
            type: BundleItemType.Feature,
          }),
      })),
    })),
  },
  content: {
    open: state.contentSectionsOpen,
    name: 'Content Items',
    list: allContent.map((c) => ({
      name: c.name,
      bundleContent: bundles.map((b) => ({
        bundleId: b.id,
        disabled: PremiumBundleContentState(b, c.id, bundles),
        checked: b.items.some((i) => i.contentId === c.id),
        onChange: () =>
          dispatch('toggleBundleContent', {
            bundleId: b.id,
            contentId: c.id,
            type: BundleItemType.Content,
          }),
      })),
    })),
  },
});

const PremiumBundleFeatureState = (
  bundle: SubscriptionBundle,
  featureId: number,
  bundles: SubscriptionBundle[],
) => {
  if (!bundle.isPlatformBundle) return false;

  if (bundle.items.some((i) => i.itemId === featureId)) return false;

  const exclusiveBundle = bundles.find((b) => !b.isPlatformBundle);

  return (
    exclusiveBundle &&
    !exclusiveBundle.items.some((i) => i.itemId === featureId)
  );
};

const PremiumBundleContentState = (
  bundle: SubscriptionBundle,
  contentId: string,
  bundles: SubscriptionBundle[],
) => {
  if (!bundle.isPlatformBundle) return false;

  if (bundle.items.some((i) => i.contentId === contentId)) return false;

  const exclusiveBundle = bundles.find((b) => !b.isPlatformBundle);

  return (
    exclusiveBundle &&
    !exclusiveBundle.items.some((i) => i.contentId === contentId)
  );
};

const platformBundleTooltipText = [
  'This is the general Playbook users that has access to all creators’ content as part of their subscription. You can decide how much of yours you want to allow them to access.',
  "New users will not be able to purchase this bundle. Users who already subscribed to Playbook's platform product will have access to this content.",
];
export const toBundleCards = (
  dispatch: BundlesVMDispatcher,
  bundles: SubscriptionBundle[],
): BundlesCards => {
  return bundles.map((b) => ({
    name: b.title,
    price: toPriceText(b.price),
    showFreeTrial: !b.isPlatformBundle,
    bundleId: b.id,
    tooltipText: b.isPlatformBundle ? platformBundleTooltipText : undefined,
    freeTrial: {
      monthly: {
        checked: b.freeTrial.monthly,
        onChange: () =>
          dispatch('toggleBundleFreeTrial', {
            bundleId: b.id,
            type: 'monthly',
          }),
      },
      annual: {
        checked: b.freeTrial.annual,
        onChange: () =>
          dispatch('toggleBundleFreeTrial', { bundleId: b.id, type: 'annual' }),
      },
    },
  }));
};

const toPriceText = (price: SubscriptionBundlePrice): string => {
  if (price?.monthly && price?.annual)
    return `$ ${priceFormatter(price.monthly)} / month • $ ${priceFormatter(
      price.annual,
    )} / year`;
  if (price.monthly) return `$ ${priceFormatter(price.monthly)} / month`;
  if (price.annual) `$ ${priceFormatter(price.annual)} / year`;
  return '';
};

const priceFormatter = (price: number) =>
  new Intl.NumberFormat('en-US', {
    maximumFractionDigits: 2,
    minimumFractionDigits: 0,
  }).format(price);
